// KitchenTourModal — immersive full-screen virtual-tour viewer ("Take me there!")
// with a per-school equipment sidebar (Küche / IT toggle) + floating detail card.
// Inventory is read live from the existing inventory APIs, filtered to this school:
//   Küche → ops.NonFoodInventar via useInventarNonFood(school.id)
//   IT    → fm.ITInventar     via useInventarIT(school.abkuerzung)
// Driven by the school's linked tour embed URL (3DVista on Azure Blob/CDN). Renders
// nothing until a school WITH an embedUrl is passed. Immersive dark surface (media-player style) —
// intentionally bespoke chrome, not the light MD3 data-panel styling.
import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import { Segmented } from '../../../../shared/ui'
import { useInventarNonFood, type NonFoodItem } from '../../../schulen/api/inventarNonFoodApi'
import { useInventarIT, type ITItem } from '../../../schulen/api/inventarITApi'

export interface KitchenTourSchool {
  id: string
  name: string
  /** Hosted virtual-tour URL (3DVista export on Azure Blob/CDN). */
  embedUrl: string
  /** School abbreviation — IT inventory filter key (fm.ITInventar.Ort). */
  abkuerzung: string | null
}

type Source = 'kueche' | 'it'

interface TourItem {
  key: string
  name: string
  category: string
  icon: string
  room: string | null
  rows: [string, string][]
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return '—'
  const dt = new Date(d)
  return Number.isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString('de-DE')
}

function nonFoodIcon(cat: string | null): string {
  const c = (cat ?? '').toLowerCase()
  if (c.includes('messer')) return 'restaurant'
  if (c.includes('topf') || c.includes('töpfe') || c.includes('toepfe')) return 'cooking'
  if (c.includes('blech')) return 'bakery_dining'
  if (c.includes('utensil')) return 'kitchen'
  return 'kitchen'
}

function itIcon(cat: string | null): string {
  const c = (cat ?? '').toLowerCase()
  if (c.includes('laptop') || c.includes('notebook')) return 'laptop_mac'
  if (c.includes('monitor') || c.includes('bildschirm')) return 'desktop_windows'
  if (c.includes('drucker') || c.includes('print')) return 'print'
  if (c.includes('tablet') || c.includes('ipad')) return 'tablet_mac'
  if (c.includes('handy') || c.includes('smartphone') || c.includes('telefon')) return 'smartphone'
  if (c.includes('server') || c.includes('nas')) return 'dns'
  if (c.includes('router') || c.includes('switch') || c.includes('netzwerk')) return 'lan'
  return 'devices'
}

function mapNonFood(items: NonFoodItem[]): TourItem[] {
  return items.map((i) => ({
    key: `nf-${i.id}`,
    name: i.bezeichnung,
    category: i.kategorie ?? '—',
    icon: nonFoodIcon(i.kategorie),
    room: i.ort,
    rows: [
      ['Kategorie', i.kategorie ?? '—'],
      ['Menge', `${i.menge ?? 0}${i.einheit ? ' ' + i.einheit : ''}`],
      ['Ort', i.ort ?? '—'],
      ['Zustand', i.zustand ?? '—'],
      ['Anschaffung', fmtDate(i.anschaffungsdatum)],
      ...(i.bemerkung ? ([['Bemerkung', i.bemerkung]] as [string, string][]) : []),
    ],
  }))
}

function mapIT(items: ITItem[]): TourItem[] {
  return items.map((i) => ({
    key: `it-${i.fmRecordId}`,
    name: i.Typ || i.Model || i.Hersteller || 'Gerät',
    category: i.kategorie_derived || i.kategorie || '—',
    icon: itIcon(i.kategorie_derived || i.kategorie),
    room: i.raum || i.Ort,
    rows: [
      ['Hersteller', i.Hersteller ?? '—'],
      ['Modell', i.Model ?? '—'],
      ['Seriennummer', i.seriennummer_override || i.Seriennummer || '—'],
      ['Kaufdatum', fmtDate(i.einkaufsdatum)],
      ['Garantie bis', fmtDate(i.garantie_bis)],
      ['Zustand', i.zustand ?? '—'],
      ['Raum', i.raum || i.Ort || '—'],
    ],
  }))
}

export function KitchenTourModal({ school, onClose }: { school: KitchenTourSchool | null; onClose: () => void }) {
  const navigate = useNavigate()
  const [source, setSource] = useState<Source>('kueche')
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  // Lazy per-tab fetch — only the active source loads. Both gate on a non-null key.
  const nonFood = useInventarNonFood(school && source === 'kueche' ? school.id : null)
  const it = useInventarIT(school && source === 'it' ? school.abkuerzung : null)

  const items = useMemo<TourItem[]>(
    () => (source === 'kueche' ? mapNonFood(nonFood.data ?? []) : mapIT(it.data ?? [])),
    [source, nonFood.data, it.data],
  )
  const loading = source === 'kueche' ? nonFood.isLoading : it.isLoading

  // Reset the detail card when the school or source changes.
  useEffect(() => { setSelectedKey(null) }, [school?.id, source])

  // Esc closes the detail card first, then the tour.
  useEffect(() => {
    if (!school) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (selectedKey) setSelectedKey(null)
      else onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [school, selectedKey, onClose])

  if (!school || !school.embedUrl) return null
  const url = school.embedUrl
  const selected = items.find((i) => i.key === selectedKey) ?? null

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex flex-col animate-[fade-in_0.25s_ease-out]"
      style={{ background: 'rgba(4,7,13,0.94)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <span className="material-symbols-outlined shrink-0" style={{ color: '#f7943f', fontSize: 22 }}>tour</span>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.32em] text-white/45">3D-Tour</div>
            <div className="text-base font-semibold text-white truncate">{school.name}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href={url} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[13px] font-medium text-white no-underline"
             style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)' }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>open_in_new</span>Vollbild
          </a>
          <button type="button" onClick={onClose}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[13px] font-medium text-white"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)' }}>
            Schließen<span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
          </button>
        </div>
      </div>

      {/* Body: inventory sidebar | iframe + detail card */}
      <div className="flex-1 flex min-h-0 relative">
        {/* Equipment sidebar */}
        <aside className="w-72 shrink-0 flex flex-col border-r border-white/10 max-md:hidden">
          <div className="px-3 py-3 border-b border-white/10">
            <Segmented<Source>
              value={source}
              onChange={setSource}
              options={[
                { value: 'kueche', label: 'Küche', icon: 'restaurant' },
                { value: 'it', label: 'IT', icon: 'devices' },
              ]}
            />
          </div>
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/10">
            <span className="text-[11px] uppercase tracking-[0.2em] text-white/45">Inventar</span>
            <span className="text-[11px] text-white/55">{loading ? '…' : `${items.length} ${items.length === 1 ? 'Eintrag' : 'Einträge'}`}</span>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-1">
            {loading ? (
              <div className="px-3 py-6 text-center text-[12px] text-white/45">Lädt…</div>
            ) : items.length === 0 ? (
              <div className="px-3 py-6 text-center text-[12px] text-white/45">
                Kein {source === 'kueche' ? 'Küchen' : 'IT'}-Inventar für diese Schule.
              </div>
            ) : (
              items.map((d) => {
                const active = selectedKey === d.key
                return (
                  <button key={d.key} type="button" onClick={() => setSelectedKey(d.key)}
                    className="w-full flex items-center gap-3 px-2.5 py-2 rounded-lg text-left transition"
                    style={{ background: active ? 'rgba(247,148,63,0.16)' : 'transparent', border: active ? '1px solid rgba(247,148,63,0.4)' : '1px solid transparent' }}>
                    <span className="grid place-items-center w-8 h-8 rounded-lg shrink-0" style={{ background: 'rgba(255,255,255,0.08)' }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 18, color: 'rgb(254 215 170)' }}>{d.icon}</span>
                    </span>
                    <span className="min-w-0">
                      <span className="block text-[13px] font-medium text-white truncate">{d.name}</span>
                      <span className="block text-[11px] text-white/50 truncate">{d.category}{d.room ? ' · ' + d.room : ''}</span>
                    </span>
                  </button>
                )
              })
            )}
          </div>
          <div className="px-4 py-3 text-[11px] leading-relaxed text-white/40 border-t border-white/10">
            Live aus dem Inventar-Modul. Bearbeiten unter <strong className="text-white/65">Inventar</strong>.
          </div>
        </aside>

        {/* Tour iframe + floating detail card */}
        <div className="flex-1 relative overflow-hidden">
          <iframe
            title={`3D-Tour — ${school.name}`}
            src={url}
            className="absolute inset-0 w-full h-full"
            style={{ border: 0, background: '#000' }}
            allow="xr-spatial-tracking; gyroscope; accelerometer; fullscreen"
            allowFullScreen
          />
          {selected && (
            <div className="absolute top-4 right-4 w-72 rounded-2xl p-4 animate-[fade-in_0.18s_ease-out]"
                 style={{ background: 'rgba(20,29,38,0.95)', backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)', border: '1px solid rgba(255,255,255,0.15)', boxShadow: '0 12px 40px rgba(0,0,0,0.5)' }}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: 'rgb(254 215 170)' }}>{selected.category}</div>
                  <div className="text-sm font-semibold text-white">{selected.name}</div>
                </div>
                <button type="button" onClick={() => setSelectedKey(null)} aria-label="Schließen" className="text-white/55 hover:text-white">
                  <span className="material-symbols-outlined" style={{ fontSize: 18 }}>close</span>
                </button>
              </div>
              <div className="space-y-1.5 mb-3">
                {selected.rows.map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between gap-3 text-[12px]">
                    <span className="text-white/45">{k}</span>
                    <span className="text-white/90 font-medium text-right">{v}</span>
                  </div>
                ))}
              </div>
              <button type="button" onClick={() => { onClose(); navigate('/inventar') }}
                      className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg py-2 text-[12px] font-medium text-white"
                      style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>open_in_new</span>Im Inventar öffnen
              </button>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}
